import { useState } from 'react';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { apiFetch } from '@diggens/api-client';
import type { ProposalCard as Proposal } from '@diggens/types';

/**
 * A preference Diggens learned from how the person works, offered for them to accept. Its text and evidence come from the
 * server, never from a model's words, so they show as plain text. Only a click on Use this applies it: the API decides
 * the click for the signed-in person alone, and answers with the card in its new state.
 */
export function ProposalCard({ card, onDecided }: { card: Proposal; onDecided: (card: Proposal) => void }) {
  const [deciding, setDeciding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const decide = async (decision: 'accept' | 'not-now') => {
    setDeciding(true);
    setError(null);
    try {
      const decided = await apiFetch<{ card: Proposal }>(`/policies/proposals/${encodeURIComponent(card.id)}/${decision}`, { method: 'POST' });
      onDecided(decided.card);
    } catch (failure) {
      console.error('Could not decide a proposal:', failure);
      setError('Your choice was not saved. Try again.');
    } finally {
      setDeciding(false);
    }
  };

  return (
    <Paper variant="outlined" component="section" aria-label="Suggested from how you work" sx={{ p: 2, maxWidth: 800 }}>
      <Typography variant="overline" color="text.secondary">
        Suggested from how you work
      </Typography>
      <Typography>{card.text}</Typography>
      {card.evidence.length > 0 && (
        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
          {/* The same correction typed twice on one day is the same line twice, so lines are keyed by place */}
          {card.evidence.map((line, index) => (
            <Typography component="li" variant="body2" color="text.secondary" key={index}>
              {line}
            </Typography>
          ))}
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
      {card.status === 'pending' && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button size="small" variant="contained" disabled={deciding} onClick={() => decide('accept')}>
            Use this
          </Button>
          <Button size="small" variant="outlined" disabled={deciding} onClick={() => decide('not-now')}>
            Not now
          </Button>
        </Stack>
      )}
    </Paper>
  );
}
